import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SvcPostPublic, SvcGetPrivate, setSession } from '../services/userService.js';
import { toast } from 'react-toastify';

function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const phoneNumber = location.state?.mobile || '+XX ••••••••1234';
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer(prev => prev - 1), 1000);
      return () => clearInterval(interval);
    } else if (timer === 0) {
      setCanResend(true);
    }
  }, [timer]);

  const handleChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.split('').slice(0, 6);
      const newOtp = [...otp];
      digits.forEach((digit, i) => {
        if (index + i < 6 && /^\d$/.test(digit)) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      setError('');
      return;
    }

    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setOtp(newOtp.slice(0, 6));
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex]?.focus();
      setError('');
    }
  };

  const isComplete = otp.every(digit => digit !== '');

  // 🔹 NEW FUNCTION: Check profile completion and navigate accordingly
  const checkProfileCompletionAndNavigate = (userId) => {
    SvcGetPrivate('/users/profile')
      .then(profileRes => {
        const user = profileRes.data.record;
        
        // Check if essential fields are filled
        const isProfileComplete = 
          user.firstName && 
          user.mobile && 
          user.gender && 
          user.weight && 
          user.location && 
          (user.age || user.birth_year);
        
        if (isProfileComplete) {
          // Profile complete → Go to wellness-guidance
          toast.success('Welcome back!');
          navigate('/wellness-guidance', { replace: true });
        } else {
          // Profile incomplete → Go to registration
          toast.info('Please complete your profile');
          navigate('/registration', { replace: true });
        }
      })
      .catch(err => {
        console.error('Error fetching profile:', err);
        // On error, go to registration
        navigate('/registration', { replace: true });
      });
  };

  const saveRegistrationData = (userId) => {
  const pendingDataStr = localStorage.getItem('pendingRegistration');

  if (!pendingDataStr) {
    checkProfileCompletionAndNavigate(userId);
    return;
  }

  const formData = JSON.parse(pendingDataStr);

  // Prepare payload for registration API
  const payload = {
    fullName: formData.fullName,
    email: formData.email,
    password: formData.password,
    mobile: formData.mobile,
    age: formData.age,
    weight: formData.weight,
    gender: formData.gender,
    location: formData.location,
    diet: formData.diet
  };

  // CALL REGISTER API (IMPORTANT FIX)
  SvcPostPublic('/users/register', payload)
    .then(res => {
      toast.success("Profile created!");

      if (res.data?.token && res.data?._id) {
        setSession({ token: res.data.token, _id: res.data._id });
      }

      localStorage.removeItem("pendingRegistration");

      navigate('/wellness-guidance', { replace: true });
    })
    .catch(err => {
      console.error("Registration failed", err);
      toast.error(err?.response?.data?.message || "Registration failed");
      navigate('/registration', { replace: true });
    });
};


  const handleVerify = () => {
    if (!isComplete) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    setError('');

    SvcPostPublic('/users/validate-otp', { 
      mobile: phoneNumber, 
      otp: otp.join('') 
    })
      .then(res => {
        if (res.data?.status === true) {
          toast.success(res.data?.message || 'OTP Verified Successfully!');

          if (res.data?.token && res.data?._id) {
            setSession({ token: res.data.token, _id: res.data._id });
          }

          if (res.data?._id) {
            saveRegistrationData(res.data._id);
          } else {
            setTimeout(() => {
              navigate('/registration', { replace: true });
            }, 500);
          }
        } else {
          setError(res.data?.message || 'Invalid OTP');
          setOtp(['', '', '', '', '', '']);
          inputRefs.current[0]?.focus();
        }
      })
      .catch(err => {
        setError(err?.response?.data?.message || 'Something went wrong');
        toast.error(err?.response?.data?.message || 'Verification failed');
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResend = () => {
    setIsLoading(true);
    
    SvcPostPublic('/users/send-otp', { mobile: phoneNumber })
      .then(res => {
        if (res.data?.message) {
          toast.success(res.data.message || 'OTP resent successfully!');
          setTimer(30);
          setCanResend(false);
          setOtp(['', '', '', '', '', '']);
          setError('');
          inputRefs.current[0]?.focus();
        } else {
          toast.error('Failed to resend OTP');
        }
      })
      .catch(err => {
        toast.error(err?.response?.data?.message || 'Something went wrong while resending OTP');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  
  return (
    <section className="otp-section">
      <div className="otp-card">
        <header className="otp-header">
          <div className="back-btn" onClick={() => navigate(-1)}>
            <span className="material-symbols-outlined">arrow_back</span>
          </div>
          <h1 className="header-text">Verify Your Number</h1>
        </header>

        <main className="otp-main">
          <h2 className="otp-title">Enter Verification Code</h2>
          <p className="otp-subtitle">
            We've sent a 6-digit code to <span className="phone-number">{phoneNumber}</span>
          </p>

          <div className="otp-container">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength="1"
                className={`otp-input ${digit ? 'filled' : ''} ${error ? 'error' : ''}`}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={(e) => e.target.select()}
              />
            ))}
          </div>

          {error && (
            <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '-16px' }}>
              {error}
            </p>
          )}
        </main>

        <footer className="otp-footer">
          <p className="resend-text">
            {!canResend ? (
              <>Didn't receive a code? Resend in <span style={{ color: 'var(--primary)', fontWeight: '700' }}>{timer}s</span></>
            ) : (
              <>Didn't receive a code? <span className="resend-link" onClick={handleResend}>Resend</span></>
            )}
          </p>
          <button 
            className={`verify-btn ${isComplete && !isLoading ? 'active' : ''}`}
            onClick={handleVerify}
            disabled={!isComplete || isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </button>
        </footer>
      </div>
    </section>
  );
}

export default VerifyOtpPage;